/**
 * What you are working on, in the AppBar.
 *
 * The ISSUE is the thing named, with its branch underneath in smaller type —
 * because the issue is the work and the branch is how Git holds it. Picking a
 * branch was never what anybody wanted to do.
 *
 * On the default branch there is no work item, and the control says so rather
 * than showing an empty slot: reading `main` is a real state, and the way out
 * of it is to start something.
 *
 * And the way BACK is here too, which it was not for a while: the only route
 * off a work branch used to be Version Control's "Finish up", which REAPS the
 * branch database and assumes the work has merged. Somebody who just wanted to
 * read `main` for a minute had nowhere to go. Stopping work is not finishing it:
 * it changes which database this person reads and leaves the branch, its
 * commits and its working copy exactly where they are.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LaunchIcon from '@mui/icons-material/Launch'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import LockIcon from '@mui/icons-material/Lock'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import Typography from '@mui/material/Typography'
import StartWorkDialog from './StartWorkDialog'
import { ApiError, api } from '../../api'
import { usePrincipal } from '../../state/AppData'
import { viewedBaseline } from '../../state/baselineRef'
import { useProject } from '../../state/Project'
import { useToasts } from '../../state/Toasts'
import { useBaselineView } from './useBaselineView'

export default function WorkPicker() {
  const principal = usePrincipal()
  const { current, branch, issue, singleDatabase, reload } = useProject()
  const navigate = useNavigate()
  const toasts = useToasts()
  const baselineView = useBaselineView()

  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const [starting, setStarting] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [title, setTitle] = useState('')
  const [webUrl, setWebUrl] = useState('')

  // The registry stores the issue's NUMBER; its title comes from GitLab, and
  // only when there is one to name.
  useEffect(() => {
    if (!current || !issue) {
      setTitle('')
      setWebUrl('')
      return
    }
    let cancelled = false
    api
      .issue(current.slug, Number(issue))
      .then((row) => {
        if (cancelled) return
        setTitle(row.title)
        setWebUrl(row.web_url)
      })
      // A title is decoration; failing to fetch it must not break the bar. The
      // number and the branch are still shown.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [current, issue])

  if (singleDatabase || !current) return null

  // A baseline (issue #97) is its own state: not the default branch and not work
  // on an issue. It is read-only whoever is looking, so nothing here offers to
  // start work from it - the way out is back to the default branch.
  const baseline = viewedBaseline(principal.baseline, branch)
  const onDefault = !baseline && !issue && branch === current.default_branch
  const label = baseline ? `Baseline ${baseline}` : issue ? `#${issue}${title ? ` ${title}` : ''}` : branch || 'No branch'

  /**
   * Read the default branch again, leaving the work branch as it is.
   *
   * Deliberately NOT `finishWork`, which reaps the branch database and belongs
   * after a merge. This only changes which database this person reads.
   *
   * `issue: ''` clears it, where an omitted field would leave it alone - so
   * without the empty string the bar would keep naming an issue this person is
   * no longer on.
   *
   * Then home, for the same reason swapping program does: the screens hold data
   * from the branch just left, and `/parts/SEK` is not guaranteed to name
   * anything on the default branch.
   */
  const stopWork = async () => {
    setStopping(true)
    try {
      await api.setProjectState(current.slug, {
        branch: current.default_branch,
        issue: '',
      })
      await reload()
      toasts.succeeded(`Reading ${current.default_branch}`)
      navigate('/')
    } catch (error) {
      toasts.failed((error as ApiError).message)
    } finally {
      setStopping(false)
    }
  }

  return (
    <>
      <Button
        color="inherit"
        onClick={(event) => setAnchor(event.currentTarget)}
        endIcon={<ExpandMoreIcon />}
        startIcon={onDefault || baseline ? <LockIcon fontSize="small" /> : undefined}
        aria-label={
          baseline
            ? `Viewing baseline ${baseline}, read only. Go back to ${current.default_branch}`
            : issue
            ? `Working on issue ${issue}${title ? `: ${title}` : ''}, branch ${branch}. Change what you are working on`
            : `Reading ${branch}. Start work`
        }
        sx={{ textTransform: 'none', maxWidth: 360, ml: 1 }}
      >
        <Box sx={{ minWidth: 0, textAlign: 'left' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
          {issue && !baseline ? (
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, display: 'block', lineHeight: 1.2 }}
            >
              {branch}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, display: 'block', lineHeight: 1.2 }}
            >
              {/* A SANDBOX HAS NO BRANCH TO PROTECT, so 'read-only' is not
                  merely unhelpful here - it is false. Nothing publishes, the
                  database is the record, and the default branch takes edits.
                  This caption is where somebody looks to find out whether they
                  can change anything, so it has to agree with the editors. */}
              {baseline ? 'read-only baseline' : principal.sandbox ? 'sandbox — editable' : 'read-only'}
            </Typography>
          )}
        </Box>
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem
          disabled={!principal.can_write || Boolean(baseline)}
          onClick={() => {
            setAnchor(null)
            setStarting(true)
          }}
        >
          <ListItemIcon>
            <PlayArrowIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={issue ? 'Work on something else…' : 'Start work…'}
            secondary={
              baseline
                ? `Read-only baseline — go back to ${current.default_branch} first`
                : principal.can_write
                  ? undefined
                  : 'Read-only access'
            }
          />
        </MenuItem>
        {baseline ? (
          <MenuItem
            disabled={baselineView.busy}
            onClick={() => {
              setAnchor(null)
              void baselineView.back()
            }}
          >
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={`Back to ${current.default_branch}`}
              secondary="Leaves the baseline; it stays opened"
            />
          </MenuItem>
        ) : null}
        {/* Only when there is something to stop. Reading the default branch
            already IS the destination, and an item that does nothing is worse
            than one that is absent. No permission gate: reading is what a
            reader may do, and this leads to the read-only state. */}
        {!onDefault && !baseline ? (
          <MenuItem disabled={stopping} onClick={() => { setAnchor(null); void stopWork() }}>
            <ListItemIcon>
              <LockIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={stopping ? 'Stopping…' : `Stop work — read ${current.default_branch}`}
              secondary="Leaves the branch and its commits alone"
            />
          </MenuItem>
        ) : null}
        {webUrl ? (
          <MenuItem component="a" href={webUrl} target="_blank" rel="noreferrer">
            <ListItemIcon>
              <LaunchIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={`Open issue #${issue} in GitLab`} />
          </MenuItem>
        ) : null}
      </Menu>

      <StartWorkDialog open={starting} onClose={() => setStarting(false)} />
    </>
  )
}
